import { Logger, withPrefix } from '@gitlab-org/logging';
import { Implements, Service, ServiceLifetime } from '@gitlab/needle';
import type { ControllerApi } from '../../commands/tui/controller_api';
import { UserActionType } from '../../backend/backend';
import { SlashCommandHandler, SlashCommandAction } from '../slash_command_handler';

// Wording tracks review_agent's `description` in developer/2.1.0-interactive.yml; the delegation
// contract the supervisor routes on: ask explicitly, and say exactly what to review. The
// fallback covers backends without delegate_task and instances on a pre-2.1.0 flow.
export const REVIEW_PROMPT = `Delegate a review of my changes to the review sub-agent. If no such sub-agent is available, carry out the review yourself.

Review the uncommitted changes in the working tree. If the working tree is clean, review the diff between the current branch and the default branch.

Report findings in the conversation. Do not modify any code.`;

// Sub-agent selection is server-side, so /review asks for delegation instead of invoking review_agent.
@Implements(SlashCommandHandler)
@Service({
  dependencies: [Logger],
  lifetime: ServiceLifetime.Singleton,
})
export class DefaultReviewCommandHandler implements SlashCommandHandler {
  #logger: Logger;

  constructor(logger: Logger) {
    this.#logger = withPrefix(logger, '[ReviewCommandHandler]');
  }

  command = {
    name: '/review',
    description: 'Review your changes with the code review sub-agent',
    action: SlashCommandAction.Review,
  } as const;

  async toPrompt(): Promise<string | undefined> {
    return REVIEW_PROMPT;
  }

  async execute(api: ControllerApi): Promise<void> {
    this.#logger.info('Executing /review command');
    await api.sendPrompt({
      type: UserActionType.SendPrompt,
      prompt: REVIEW_PROMPT,
    });
  }
}
